import React, { useEffect, useMemo, useState } from "react";
import useCalculatorMode from "../../hooks/useCalculatorMode";
import useCalculatorResults from "../../hooks/useCalculatorResults";

const MAX_CALCULATED_POINTS = 10000;
const MAX_PREVIEW_POINTS = 400;
const PAGE_SIZE = 100;
const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};
const fmt = (value, unit) => toNumber(value).toFixed(unit === "metric" ? 3 : 4);
const rotatePoint = (x, y, degrees) => {
  const radians = (toNumber(degrees) * Math.PI) / 180;
  return {
    x: x * Math.cos(radians) - y * Math.sin(radians),
    y: x * Math.sin(radians) + y * Math.cos(radians),
  };
};

const parseManualCoordinates = (text) =>
  String(text || "")
    .split(/\n|;/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, index) => {
      const [x, y] = line.split(/,|\s+/).map(Number);
      return Number.isFinite(x) && Number.isFinite(y)
        ? { label: `P${index + 1}`, x, y }
        : null;
    })
    .filter(Boolean);

const getHoleGeometry = (params) => {
  const diameter = Math.max(0, toNumber(params.holeDiameter));
  const slotLength = Math.max(diameter, toNumber(params.slotLength) || diameter);
  const slotWidth = Math.max(0, toNumber(params.slotWidth) || diameter);
  const isSlotted = params.patternType === "slot";
  return {
    type: isSlotted ? "slot" : "round",
    diameter,
    radius: diameter / 2,
    slotLength,
    slotWidth,
  };
};

const getBounds = (point) => {
  if (point.geometry?.type === "slot") {
    return {
      minX: point.x - point.geometry.slotLength / 2,
      maxX: point.x + point.geometry.slotLength / 2,
      minY: point.y - point.geometry.slotWidth / 2,
      maxY: point.y + point.geometry.slotWidth / 2,
    };
  }

  const radius = point.geometry?.radius || 0;
  return {
    minX: point.x - radius,
    maxX: point.x + radius,
    minY: point.y - radius,
    maxY: point.y + radius,
  };
};

const boxesOverlap = (a, b, tolerance) =>
  a.minX < b.maxX - tolerance &&
  a.maxX > b.minX + tolerance &&
  a.minY < b.maxY - tolerance &&
  a.maxY > b.minY + tolerance;

const detectOverlaps = (coordinates, tolerance = 0) => {
  const overlapIndexes = new Set();
  const pairs = [];
  const roundOnly = coordinates.every((point) => point.geometry?.type !== "slot");
  const maxPairs = 500;

  const markOverlap = (i, j) => {
    overlapIndexes.add(i);
    overlapIndexes.add(j);
    if (pairs.length < maxPairs) {
      pairs.push({
        a: i,
        b: j,
        label: `${coordinates[i].label} overlaps ${coordinates[j].label}`,
      });
    }
  };

  if (roundOnly && coordinates.length > 200) {
    const maxDiameter = Math.max(...coordinates.map((point) => point.geometry.diameter || 0), 0.0001);
    const cellSize = Math.max(maxDiameter, 0.0001);
    const cells = new Map();

    coordinates.forEach((point, index) => {
      const cellX = Math.floor(point.x / cellSize);
      const cellY = Math.floor(point.y / cellSize);
      const key = `${cellX},${cellY}`;
      const bucket = cells.get(key) || [];
      bucket.push(index);
      cells.set(key, bucket);
    });

    coordinates.forEach((point, i) => {
      const cellX = Math.floor(point.x / cellSize);
      const cellY = Math.floor(point.y / cellSize);
      for (let x = cellX - 1; x <= cellX + 1; x += 1) {
        for (let y = cellY - 1; y <= cellY + 1; y += 1) {
          (cells.get(`${x},${y}`) || []).forEach((j) => {
            if (j <= i) return;
            const dx = point.x - coordinates[j].x;
            const dy = point.y - coordinates[j].y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance < (point.geometry.radius + coordinates[j].geometry.radius - tolerance)) {
              markOverlap(i, j);
            }
          });
        }
      }
    });

    return {
      overlapIndexes,
      pairs,
      pairLimitReached: pairs.length >= maxPairs,
      method: "Exact round-hole center-distance collision check using a spatial grid for large patterns",
    };
  }

  for (let i = 0; i < coordinates.length; i += 1) {
    for (let j = i + 1; j < coordinates.length; j += 1) {
      let overlaps = false;
      if (roundOnly) {
        const dx = coordinates[i].x - coordinates[j].x;
        const dy = coordinates[i].y - coordinates[j].y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        overlaps = distance < (coordinates[i].geometry.radius + coordinates[j].geometry.radius - tolerance);
      } else {
        overlaps = boxesOverlap(getBounds(coordinates[i]), getBounds(coordinates[j]), tolerance);
      }

      if (overlaps) {
        markOverlap(i, j);
      }
    }
  }

  return {
    overlapIndexes,
    pairs,
    pairLimitReached: pairs.length >= maxPairs,
    method: roundOnly
      ? "Exact round-hole center-distance collision check"
      : "Approximate bounding-box collision check for slotted/non-round geometry",
  };
};

const buildCoordinates = (params) => {
  const unit = params.unit || "inch";
  const errors = [];
  const warnings = [];
  const points = [];
  const patternType = params.patternType || "circular";
  const mirrorX = params.mirrorX === "yes";
  const mirrorY = params.mirrorY === "yes";
  const rotation = toNumber(params.rotationAngle);
  const centerX = toNumber(params.centerX);
  const centerY = toNumber(params.centerY);
  const geometry = getHoleGeometry(params);

  const pushPoint = (x, y, label) => {
    const mirroredX = mirrorX ? -x : x;
    const mirroredY = mirrorY ? -y : y;
    const rotated = rotatePoint(mirroredX, mirroredY, rotation);
    points.push({
      label,
      x: Number(fmt(rotated.x + centerX, unit)),
      y: Number(fmt(rotated.y + centerY, unit)),
      geometry,
    });
  };

  const holeDiameter = toNumber(params.holeDiameter);
  if (holeDiameter <= 0) warnings.push("Hole diameter is not used for every layout, but should be greater than zero for shop output.");

  if (patternType === "circular") {
    const count = Math.floor(toNumber(params.holeCount));
    const diameter = params.diameterMode === "radius" ? toNumber(params.patternRadius) * 2 : toNumber(params.patternDiameter);
    if (count <= 0) errors.push("Hole count must be greater than zero.");
    if (count > MAX_CALCULATED_POINTS) errors.push(`Hole count is limited to ${MAX_CALCULATED_POINTS.toLocaleString()} points.`);
    if (diameter <= 0) errors.push("Pattern diameter/radius must be greater than zero.");
    if (!errors.length) {
      const radius = diameter / 2;
      const start = toNumber(params.startAngle);
      for (let index = 0; index < count; index += 1) {
        const angle = ((start + (360 / count) * index) * Math.PI) / 180;
        pushPoint(radius * Math.cos(angle), radius * Math.sin(angle), `H${index + 1}`);
      }
    }
  }

  if (patternType === "linear" || patternType === "slot") {
    const count = Math.floor(toNumber(params.holeCount));
    const spacing = toNumber(params.spacingX);
    if (count <= 0) errors.push("Hole count must be greater than zero.");
    if (count > MAX_CALCULATED_POINTS) errors.push(`Hole count is limited to ${MAX_CALCULATED_POINTS.toLocaleString()} points.`);
    if (spacing <= 0) errors.push("Spacing must be greater than zero.");
    if (!errors.length) {
      const start = -((count - 1) * spacing) / 2;
      for (let index = 0; index < count; index += 1) {
        pushPoint(start + index * spacing, 0, patternType === "slot" ? `SLOT${index + 1}` : `H${index + 1}`);
      }
    }
  }

  if (patternType === "rectangular") {
    const length = toNumber(params.rectLength);
    const width = toNumber(params.rectWidth);
    if (length <= 0 || width <= 0) errors.push("Rectangle length and width must be greater than zero.");
    if (!errors.length) {
      pushPoint(-length / 2, -width / 2, "C1");
      pushPoint(length / 2, -width / 2, "C2");
      pushPoint(length / 2, width / 2, "C3");
      pushPoint(-length / 2, width / 2, "C4");
    }
  }

  if (patternType === "grid") {
    const rows = Math.floor(toNumber(params.rows));
    const columns = Math.floor(toNumber(params.columns));
    const spacingX = toNumber(params.spacingX);
    const spacingY = toNumber(params.spacingY);
    const total = rows * columns;
    if (rows <= 0 || columns <= 0) errors.push("Rows and columns must be greater than zero.");
    if (total > MAX_CALCULATED_POINTS) errors.push(`Grid is limited to ${MAX_CALCULATED_POINTS.toLocaleString()} total points.`);
    if (spacingX <= 0 || spacingY <= 0) errors.push("X and Y spacing must be greater than zero.");
    if (!errors.length) {
      const startX = -((columns - 1) * spacingX) / 2;
      const startY = -((rows - 1) * spacingY) / 2;
      for (let row = 0; row < rows; row += 1) {
        for (let col = 0; col < columns; col += 1) {
          pushPoint(startX + col * spacingX, startY + row * spacingY, `R${row + 1}C${col + 1}`);
        }
      }
    }
  }

  if (patternType === "ellipse") {
    const count = Math.floor(toNumber(params.holeCount));
    const major = toNumber(params.majorDiameter);
    const minor = toNumber(params.minorDiameter);
    if (count <= 0) errors.push("Hole count must be greater than zero.");
    if (count > MAX_CALCULATED_POINTS) errors.push(`Hole count is limited to ${MAX_CALCULATED_POINTS.toLocaleString()} points.`);
    if (major <= 0 || minor <= 0) errors.push("Major and minor diameters must be greater than zero.");
    if (!errors.length) {
      const start = toNumber(params.startAngle);
      for (let index = 0; index < count; index += 1) {
        const angle = ((start + (360 / count) * index) * Math.PI) / 180;
        pushPoint((major / 2) * Math.cos(angle), (minor / 2) * Math.sin(angle), `H${index + 1}`);
      }
      warnings.push("Elliptical bolt patterns are layout geometry only; verify functional fit and manufacturing intent.");
    }
  }

  if (patternType === "manual" || patternType === "irregular") {
    const manual = parseManualCoordinates(params.manualCoordinates);
    if (!manual.length) errors.push("Enter manual coordinates as X,Y pairs, one per line.");
    if (manual.length > MAX_CALCULATED_POINTS) errors.push(`Manual coordinates are limited to ${MAX_CALCULATED_POINTS.toLocaleString()} points.`);
    if (!errors.length) manual.forEach((point) => pushPoint(point.x, point.y, point.label));
  }

  if (points.length > 1000) {
    warnings.push("Large coordinate table calculated. Preview is capped and table is paginated to keep the app responsive.");
  }

  const overlapResult = detectOverlaps(points, Math.max(0, toNumber(params.overlapTolerance)));
  const coordinates = points.map((point, index) => ({
    ...point,
    overlaps: overlapResult.overlapIndexes.has(index),
  }));
  if (overlapResult.pairs.length > 0) {
    warnings.push("Overlapping holes detected.");
  }
  if (overlapResult.pairLimitReached) {
    warnings.push("Overlap pair list is capped for performance; highlighted holes still show detected overlap status.");
  }

  return {
    errors,
    warnings,
    coordinates,
    overlapPairs: overlapResult.pairs,
    overlapMethod: overlapResult.method,
    hasOverlaps: overlapResult.pairs.length > 0,
    unit,
    formulas: [
      "Circular: X = centerX + radius x cos(angle), Y = centerY + radius x sin(angle)",
      "Grid/linear: coordinates use spacing from the pattern center, then mirror and rotation transforms are applied",
      "Rotation: X' = X cos(theta) - Y sin(theta), Y' = X sin(theta) + Y cos(theta)",
    ],
    insight: points.length
      ? `${points.length.toLocaleString()} coordinates calculated. Verify datum, center reference, rotation direction, and print coordinate convention before programming.`
      : "Enter pattern data to generate coordinates.",
  };
};

const BoltPatternCalculator = ({ onSave }) => {
  const [params, setParams] = useState({
    unit: "inch",
    patternType: "circular",
    diameterMode: "diameter",
    holeCount: 4,
    holeDiameter: 0.25,
    patternDiameter: 4,
    patternRadius: 2,
    startAngle: 0,
    centerX: 0,
    centerY: 0,
    rotationAngle: 0,
    mirrorX: "no",
    mirrorY: "no",
    rows: 3,
    columns: 4,
    spacingX: 1,
    spacingY: 1,
    slotLength: 1,
    slotWidth: 0.25,
    rectLength: 4,
    rectWidth: 2,
    majorDiameter: 5,
    minorDiameter: 3,
    overlapTolerance: 0,
    manualCoordinates: "0,0\n1,0\n1,1\n0,1",
  });
  const [page, setPage] = useState(0);
  const { isAdvanced, mode, toggleMode } = useCalculatorMode();
  const { saveResult } = useCalculatorResults("bolt-pattern", params.unit === "metric" ? "metric" : "imperial");

  const results = useMemo(() => buildCoordinates(params), [params]);
  const hasErrors = results.errors.length > 0;
  const totalPages = Math.max(1, Math.ceil(results.coordinates.length / PAGE_SIZE));
  const pageCoordinates = results.coordinates.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const previewCoordinates = results.coordinates.slice(0, MAX_PREVIEW_POINTS);
  const unitLabel = params.unit === "metric" ? "mm" : "in";
  const centerReference = `X${fmt(params.centerX, params.unit)} Y${fmt(params.centerY, params.unit)}`;
  const csvHeader = "Hole #,Label,Pattern Type,Hole Diameter,Center Reference,X,Y,Overlap";
  const coordinateCsvRows = results.coordinates.map((point, index) =>
    [
      index + 1,
      point.label,
      params.patternType,
      fmt(params.holeDiameter, params.unit),
      centerReference,
      point.x,
      point.y,
      point.overlaps ? "YES" : "NO",
    ].join(",")
  );

  useEffect(() => {
    if (hasErrors || !results.coordinates.length) return;
    saveResult({
      calculatorId: "bolt-pattern",
      calculatorName: "Bolt Pattern",
      inputValues: params,
      outputValues: {
        coordinates: results.coordinates,
        holeCount: results.coordinates.length,
        overlapPairs: results.overlapPairs,
        hasOverlaps: results.hasOverlaps,
      },
      units: params.unit === "metric" ? "metric" : "imperial",
      operationType: params.patternType,
      warnings: results.warnings,
      sourceFormulaReferences: results.formulas,
    });
  }, [hasErrors, params, results, saveResult]);

  const update = (field, value) => {
    setParams((current) => ({ ...current, [field]: value }));
    setPage(0);
  };

  const copyText = () => {
    const lines = [
      ...(results.hasOverlaps ? ["WARNING: Overlapping holes detected."] : []),
      csvHeader,
      ...coordinateCsvRows,
    ];
    navigator.clipboard?.writeText(lines.join("\n"));
  };

  const copyGCode = () => {
    const lines = [
      params.unit === "metric" ? "G21" : "G20",
      "G90",
      ...(results.hasOverlaps ? ["(WARNING: OVERLAPPING HOLES DETECTED)"] : []),
      ...results.coordinates.map((point) => `X${point.x} Y${point.y}`),
    ];
    navigator.clipboard?.writeText(lines.join("\n"));
  };

  const downloadCsv = () => {
    const csv = [csvHeader, ...coordinateCsvRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `manucalc-${params.patternType}-bolt-pattern.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const saveCalculation = () => {
    if (!onSave || hasErrors) return;
    onSave({
      summary: `${params.patternType} bolt pattern - ${results.coordinates.length} holes`,
      input: params,
      results,
      coordinates: results.coordinates,
      overlapPairs: results.overlapPairs,
      warnings: results.warnings,
      formulas: results.formulas,
    });
  };

  const renderPreview = () => {
    if (!previewCoordinates.length) return null;
    const bounds = previewCoordinates.map(getBounds);
    const minX = Math.min(...bounds.map((box) => box.minX), toNumber(params.centerX));
    const maxX = Math.max(...bounds.map((box) => box.maxX), toNumber(params.centerX));
    const minY = Math.min(...bounds.map((box) => box.minY), toNumber(params.centerY));
    const maxY = Math.max(...bounds.map((box) => box.maxY), toNumber(params.centerY));
    const width = Math.max(maxX - minX, 0.0001);
    const height = Math.max(maxY - minY, 0.0001);
    const size = 420;
    const pad = 44;
    const scale = Math.min((size - pad * 2) / width, (size - pad * 2) / height);
    const project = (point) => ({
      x: pad + (point.x - minX) * scale,
      y: size - pad - (point.y - minY) * scale,
    });
    const center = project({ x: toNumber(params.centerX), y: toNumber(params.centerY) });
    const axisX1 = project({ x: minX, y: toNumber(params.centerY) });
    const axisX2 = project({ x: maxX, y: toNumber(params.centerY) });
    const axisY1 = project({ x: toNumber(params.centerX), y: minY });
    const axisY2 = project({ x: toNumber(params.centerX), y: maxY });

    return (
      <div className="bolt-preview-panel">
        <h4>Visual Preview</h4>
        <svg viewBox={`0 0 ${size} ${size}`} className="bolt-preview-svg">
          <line className="bolt-axis" x1={axisX1.x} y1={axisX1.y} x2={axisX2.x} y2={axisX2.y} />
          <line className="bolt-axis" x1={axisY1.x} y1={axisY1.y} x2={axisY2.x} y2={axisY2.y} />
          <circle className="bolt-center" cx={center.x} cy={center.y} r="4" />
          {previewCoordinates.map((point, index) => {
            const projected = project(point);
            const className = point.overlaps ? "bolt-hole bolt-hole-overlap" : "bolt-hole";
            if (point.geometry?.type === "slot") {
              const slotWidth = Math.max(point.geometry.slotWidth * scale, 2);
              const slotLength = Math.max(point.geometry.slotLength * scale, slotWidth);
              return (
                <rect
                  key={`${point.label}-${index}`}
                  className={className}
                  x={projected.x - slotLength / 2}
                  y={projected.y - slotWidth / 2}
                  width={slotLength}
                  height={slotWidth}
                  rx={slotWidth / 2}
                />
              );
            }
            return (
              <circle
                key={`${point.label}-${index}`}
                className={className}
                cx={projected.x}
                cy={projected.y}
                r={Math.max(point.geometry.radius * scale, 1.5)}
              />
            );
          })}
        </svg>
        <p>Scale: 1 {unitLabel} = {scale.toFixed(2)} screen units. Center reference shown at {centerReference}.</p>
        <p>Overlap method: {results.overlapMethod}.</p>
        {results.coordinates.length > MAX_PREVIEW_POINTS && (
          <p>Preview capped at {MAX_PREVIEW_POINTS} points. Full coordinate table and CSV include all calculated points.</p>
        )}
      </div>
    );
  };

  return (
    <div className="calculator-page">
      <div className="calculator-shell">
        <div className="calculator-header">
          <h1>Bolt Pattern Calculator</h1>
          <p>
            Generate circular, linear, rectangular, grid, slotted, elliptical, and manual coordinate bolt patterns with copy, CSV, G-code coordinate output, and guarded large-table handling.
          </p>
        </div>

        <div className="calculator-layout calculator-layout-wide-results">
          <div className="calculator-panel">
            <div className="calculator-mode-row">
              <span>{mode === "advanced" ? "Advanced Mode" : "Beginner Mode"}</span>
              <button type="button" className="btn-secondary" onClick={toggleMode}>
                {isAdvanced ? "Use Beginner Mode" : "Use Advanced Mode"}
              </button>
            </div>
            <h3>Pattern Setup</h3>
            <div className="calculator-input-grid">
              <label className="form-group">Units
                <select value={params.unit} onChange={(event) => update("unit", event.target.value)}>
                  <option value="inch">Inch</option>
                  <option value="metric">Metric</option>
                </select>
              </label>
              <label className="form-group">Pattern Type
                <select value={params.patternType} onChange={(event) => update("patternType", event.target.value)}>
                  <option value="circular">Circular</option>
                  <option value="linear">Linear</option>
                  <option value="rectangular">Rectangular corners</option>
                  <option value="grid">Grid</option>
                  <option value="slot">Slotted centers</option>
                  <option value="ellipse">Elliptical</option>
                  <option value="manual">Custom / Manual</option>
                  <option value="irregular">Irregular</option>
                </select>
              </label>
              <label className="form-group">Hole Diameter ({unitLabel})
                <input type="number" value={params.holeDiameter} onChange={(event) => update("holeDiameter", event.target.value)} />
              </label>
              {isAdvanced && (
                <>
                  <label className="form-group">Center X ({unitLabel})
                    <input type="number" value={params.centerX} onChange={(event) => update("centerX", event.target.value)} />
                  </label>
                  <label className="form-group">Center Y ({unitLabel})
                    <input type="number" value={params.centerY} onChange={(event) => update("centerY", event.target.value)} />
                  </label>
                  <label className="form-group">Rotation Angle
                    <input type="number" value={params.rotationAngle} onChange={(event) => update("rotationAngle", event.target.value)} />
                  </label>
                  <label className="form-group">Mirror X
                    <select value={params.mirrorX} onChange={(event) => update("mirrorX", event.target.value)}>
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </label>
                  <label className="form-group">Mirror Y
                    <select value={params.mirrorY} onChange={(event) => update("mirrorY", event.target.value)}>
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </label>
                  <label className="form-group">Overlap Tolerance ({unitLabel})
                    <input type="number" value={params.overlapTolerance} onChange={(event) => update("overlapTolerance", event.target.value)} />
                  </label>
                </>
              )}
            </div>

            <h3>Pattern Dimensions</h3>
            <div className="calculator-input-grid">
              {["circular", "linear", "slot", "ellipse"].includes(params.patternType) && (
                <label className="form-group">Hole Count
                  <input type="number" value={params.holeCount} onChange={(event) => update("holeCount", event.target.value)} />
                </label>
              )}
              {params.patternType === "circular" && (
                <>
                  <label className="form-group">Diameter or Radius
                    <select value={params.diameterMode} onChange={(event) => update("diameterMode", event.target.value)}>
                      <option value="diameter">Pattern Diameter</option>
                      <option value="radius">Pattern Radius</option>
                    </select>
                  </label>
                  {params.diameterMode === "diameter" ? (
                    <label className="form-group">Pattern Diameter ({unitLabel})
                      <input type="number" value={params.patternDiameter} onChange={(event) => update("patternDiameter", event.target.value)} />
                    </label>
                  ) : (
                    <label className="form-group">Pattern Radius ({unitLabel})
                      <input type="number" value={params.patternRadius} onChange={(event) => update("patternRadius", event.target.value)} />
                    </label>
                  )}
                  <label className="form-group">Start Angle
                    <input type="number" value={params.startAngle} onChange={(event) => update("startAngle", event.target.value)} />
                  </label>
                </>
              )}
              {["linear", "slot"].includes(params.patternType) && (
                <label className="form-group">Spacing ({unitLabel})
                  <input type="number" value={params.spacingX} onChange={(event) => update("spacingX", event.target.value)} />
                </label>
              )}
              {params.patternType === "slot" && isAdvanced && (
                <>
                  <label className="form-group">Slot Length ({unitLabel})
                    <input type="number" value={params.slotLength} onChange={(event) => update("slotLength", event.target.value)} />
                  </label>
                  <label className="form-group">Slot Width ({unitLabel})
                    <input type="number" value={params.slotWidth} onChange={(event) => update("slotWidth", event.target.value)} />
                  </label>
                </>
              )}
              {params.patternType === "rectangular" && (
                <>
                  <label className="form-group">Rectangle Length ({unitLabel})
                    <input type="number" value={params.rectLength} onChange={(event) => update("rectLength", event.target.value)} />
                  </label>
                  <label className="form-group">Rectangle Width ({unitLabel})
                    <input type="number" value={params.rectWidth} onChange={(event) => update("rectWidth", event.target.value)} />
                  </label>
                </>
              )}
              {params.patternType === "grid" && (
                <>
                  <label className="form-group">Rows
                    <input type="number" value={params.rows} onChange={(event) => update("rows", event.target.value)} />
                  </label>
                  <label className="form-group">Columns
                    <input type="number" value={params.columns} onChange={(event) => update("columns", event.target.value)} />
                  </label>
                  <label className="form-group">X Spacing ({unitLabel})
                    <input type="number" value={params.spacingX} onChange={(event) => update("spacingX", event.target.value)} />
                  </label>
                  <label className="form-group">Y Spacing ({unitLabel})
                    <input type="number" value={params.spacingY} onChange={(event) => update("spacingY", event.target.value)} />
                  </label>
                </>
              )}
              {params.patternType === "ellipse" && (
                <>
                  <label className="form-group">Major Diameter ({unitLabel})
                    <input type="number" value={params.majorDiameter} onChange={(event) => update("majorDiameter", event.target.value)} />
                  </label>
                  <label className="form-group">Minor Diameter ({unitLabel})
                    <input type="number" value={params.minorDiameter} onChange={(event) => update("minorDiameter", event.target.value)} />
                  </label>
                  <label className="form-group">Start Angle
                    <input type="number" value={params.startAngle} onChange={(event) => update("startAngle", event.target.value)} />
                  </label>
                </>
              )}
              {["manual", "irregular"].includes(params.patternType) && (
                <label className="form-group bolt-manual-field">Manual Coordinates
                  <textarea value={params.manualCoordinates} onChange={(event) => update("manualCoordinates", event.target.value)} />
                </label>
              )}
            </div>

            <div className="calculator-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setParams({
                unit: "inch", patternType: "circular", diameterMode: "diameter", holeCount: 4, holeDiameter: 0.25, patternDiameter: 4, patternRadius: 2, startAngle: 0, centerX: 0, centerY: 0, rotationAngle: 0, mirrorX: "no", mirrorY: "no", rows: 3, columns: 4, spacingX: 1, spacingY: 1, slotLength: 1, slotWidth: 0.25, rectLength: 4, rectWidth: 2, majorDiameter: 5, minorDiameter: 3, overlapTolerance: 0, manualCoordinates: "0,0\n1,0\n1,1\n0,1",
              })}>Reset</button>
              <button type="button" className="btn btn-secondary" onClick={copyText} disabled={hasErrors}>Copy CSV Text</button>
              <button type="button" className="btn btn-secondary" onClick={copyGCode} disabled={hasErrors}>Copy G-code Points</button>
              <button type="button" className="btn btn-secondary" onClick={downloadCsv} disabled={hasErrors}>Download CSV</button>
              <button type="button" className="btn btn-primary" onClick={saveCalculation} disabled={hasErrors}>Save Calculation</button>
            </div>
          </div>

          <div className="calculator-panel">
            <h3>Results</h3>
            {hasErrors ? (
              <div className="warnings">
                <h4>Check Inputs</h4>
                <ul>{results.errors.map((error) => <li key={error}>{error}</li>)}</ul>
              </div>
            ) : (
              <>
                <div className="calculator-results-grid">
                  <div className="calculator-result-card"><span className="calculator-result-label">Pattern Type</span><strong className="calculator-result-value">{params.patternType}</strong></div>
                  <div className="calculator-result-card"><span className="calculator-result-label">Coordinates</span><strong className="calculator-result-value">{results.coordinates.length.toLocaleString()}</strong></div>
                  <div className="calculator-result-card"><span className="calculator-result-label">Hole Diameter</span><strong className="calculator-result-value">{fmt(params.holeDiameter, params.unit)} {unitLabel}</strong></div>
                  <div className="calculator-result-card"><span className="calculator-result-label">Center Reference</span><strong className="calculator-result-value">{centerReference}</strong></div>
                  <div className="calculator-result-card"><span className="calculator-result-label">Unit</span><strong className="calculator-result-value">{unitLabel}</strong></div>
                  <div className="calculator-result-card"><span className="calculator-result-label">Overlaps</span><strong className="calculator-result-value">{results.overlapPairs.length}</strong></div>
                </div>
                <section className="calculator-result-card bolt-insight">
                  <span className="calculator-result-label">Manufacturing Insight</span>
                  <p>{results.insight}</p>
                </section>
                {renderPreview()}
                {results.hasOverlaps && (
                  <div className="warnings bolt-overlap-warning">
                    <h4>Overlapping holes detected.</h4>
                    <ul>
                      {results.overlapPairs.map((pair) => <li key={`${pair.a}-${pair.b}`}>{pair.label}</li>)}
                    </ul>
                  </div>
                )}
                {results.warnings.length > 0 && (
                  <div className="warnings">
                    <h4>Warnings</h4>
                    <ul>{results.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul>
                  </div>
                )}
                <div className="bolt-table-wrap">
                  <table className="bolt-coordinate-table">
                    <thead><tr><th>#</th><th>Label</th><th>X ({unitLabel})</th><th>Y ({unitLabel})</th><th>Overlap</th></tr></thead>
                    <tbody>
                      {pageCoordinates.map((point, index) => (
                        <tr key={`${point.label}-${page}-${index}`} className={point.overlaps ? "bolt-row-overlap" : ""}>
                          <td>{page * PAGE_SIZE + index + 1}</td>
                          <td>{point.label}</td>
                          <td>{point.x}</td>
                          <td>{point.y}</td>
                          <td>{point.overlaps ? "YES" : "NO"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="bolt-pagination">
                  <button type="button" onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}>Previous</button>
                  <span>Page {page + 1} of {totalPages}</span>
                  <button type="button" onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}>Next</button>
                </div>
              </>
            )}

            <section style={{ marginTop: "18px" }}>
              <h4>Formula Used</h4>
              <ul style={{ color: "var(--text-secondary)", paddingLeft: "18px" }}>
                {results.formulas.map((formula) => <li key={formula}>{formula}</li>)}
              </ul>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoltPatternCalculator;
